import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { useClientFromParam } from '../hooks/useClientFromParam';
import { getPdfContent, PdfPendingError } from '@/db/services/document_service';
import PdfPendingNotice, { PdfPendingVariant } from '@/components/PdfPendingNotice';
import { decorateClient } from '@/db/types/client';
import db from '@/db/db';
import { trackEvent } from '@/db/services/analytics_service';
import { createLogger, LogLevel } from '../lib/logger';
import { ExclamationTriangleIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon as ExclamationTriangleIconSolid } from '@heroicons/react/20/solid';

const logger = createLogger('DocumentPdf', LogLevel.DEBUG);

// How long to wait before the single automatic re-check when the server
// reports the PDF render is still pending.
const PDF_PENDING_AUTO_RECHECK_MS = 10_000;

export default function DocumentPdf() {
  const { documentId, localId } = useParams<{ id: string; documentId?: string; localId?: string }>();
  const navigate = useNavigate();
  const { client, clientId, clientSlug, loading: clientLoading } = useClientFromParam();
  const isDraftRoute = Boolean(localId);
  const documentIdNum = documentId ? parseInt(documentId, 10) : null;

  // PDF content state
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The PDF isn't ready yet (server still rendering, or offline before it was
  // downloaded) — a normal transient state, shown neutrally rather than as a
  // failure. 'checking' means a poll is actively re-requesting right now.
  const [pdfPending, setPdfPending] = useState<PdfPendingVariant | false>(false);
  // Retry the PDF fetch in-place. Reloading the whole page triggers a
  // re-init of the lock/auth pipeline against an empty in-memory state
  // and ends up logging the user out — keep the retry inside React.
  const [retryCount, setRetryCount] = useState(0);
  const autoRecheckedRef = useRef(false);

  // Decorate client with computed properties
  const decoratedClient = useMemo(() => {
    return client ? decorateClient(client) : client;
  }, [client]);

  // Use useLiveQuery to get document from the database
  // Returns: undefined = still loading, null = not found, Document = found
  const document = useLiveQuery(async () => {
    if (isDraftRoute) {
      // Draft route - query by local_id
      if (!localId) return undefined;
      return (await db.documents.where('local_id').equals(localId).first()) ?? null;
    } else {
      // Server route - query by id
      if (!documentIdNum || isNaN(documentIdNum)) return undefined;
      return (await db.documents.get(documentIdNum)) ?? null;
    }
  }, [isDraftRoute, localId, documentIdNum]);

  // Redirect from draft URL to server URL after sync completes
  useEffect(() => {
    if (isDraftRoute && document && document.synced && document.id && clientSlug) {
      logger.info(`Draft ${localId} synced with server ID ${document.id}, redirecting...`);
      navigate(`/clients/${clientSlug}/documents/${document.id}/pdf`, { replace: true });
    }
  }, [isDraftRoute, document, localId, clientSlug, navigate]);

  // Fetch PDF content when document loads or changes
  useEffect(() => {
    if (!document) return;

    const abortController = new AbortController();

    // Track PDF viewing
    trackEvent('document.pdf.viewed', {
      document_id: document.id,
      client_id: clientId ?? undefined,
      template_id: document.template_id,
    }).catch(err => {
      logger.error('Failed to track document.pdf.viewed event:', err);
    });

    // Fetch PDF content
    const loadPdf = async () => {
      setIsLoading(true);
      setError(null);
      setPdfPending(false);

      try {
        const content = await getPdfContent(
          typeof document.id === 'number' ? document.id : undefined,
          document.links?.pdf,
          {
            // Abort cancels the in-flight fetch and any render-poll wait, so
            // navigating away (or tapping retry) doesn't leave a poll running.
            signal: abortController.signal,
            // First 202: swap the anonymous spinner for the "still being
            // prepared" panel while the poll keeps checking underneath.
            onPending: () => {
              if (!abortController.signal.aborted) setPdfPending('checking');
            },
          }
        );

        if (abortController.signal.aborted) return;

        if (content) {
          setPdfPending(false);
          setPdfData(content);
        } else if (!navigator.onLine) {
          // No cached copy and no connection — same user situation as going
          // offline mid-poll, so show the same guidance instead of an error.
          setPdfPending('offline');
        } else {
          setPdfPending(false);
          setError('PDF content not available. Please check your connection and try again.');
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (err instanceof PdfPendingError) {
          setPdfPending(err.reason === 'offline' ? 'offline' : 'ready-later');
          return;
        }
        setPdfPending(false);
        logger.error('Error loading PDF:', err);
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void loadPdf();

    return () => { abortController.abort(); };
  }, [document, clientId, retryCount]);

  // A new document gets its own auto-recheck allowance.
  const documentKey = typeof document?.id === 'number' ? document.id : localId;
  useEffect(() => {
    autoRecheckedRef.current = false;
  }, [documentKey]);

  // The client knows the render is merely pending, so check once more on the
  // user's behalf before asking them to. Only after a poll has given up
  // ('ready-later' — while 'checking', the poll is already re-requesting),
  // and once per document; after that it's the manual "Check Again" button.
  useEffect(() => {
    if (pdfPending !== 'ready-later' || autoRecheckedRef.current) return;
    autoRecheckedRef.current = true;
    const timer = setTimeout(() => setRetryCount((n) => n + 1), PDF_PENDING_AUTO_RECHECK_MS);
    return () => clearTimeout(timer);
  }, [pdfPending]);

  // Loading state
  const loading = clientLoading || document === undefined;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading PDF...</p>
        </div>
      </div>
    );
  }

  // Client not found or wrong organization
  if (client === null) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Client Not Found</h2>
          <p className="text-gray-600 mb-4">The client you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate('/clients')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  // Document not found
  if (document === null || !document) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Document Not Found</h2>
          <p className="text-gray-600 mb-4">The document you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(`/clients/${clientSlug}`)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            Back to Client
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="flex items-center px-4 py-3 border-b bg-white shadow-sm">
        <button
          type="button"
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate(`/clients/${clientSlug}`)}
          className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Go back"
          aria-label="Go back"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-grow">
          <h1 className="text-lg font-semibold text-gray-900">{document.name}</h1>
          {decoratedClient && (
            <p className="text-sm text-gray-500">{decoratedClient.person.displayName}</p>
          )}
        </div>
        {!navigator.onLine && (
          <span className="text-sm text-amber-600 flex items-center">
            <ExclamationTriangleIconSolid className="w-4 h-4 mr-1" />
            Offline
          </span>
        )}
      </div>

      {/* PDF content */}
      <div className="flex-grow relative">
        {isLoading && !pdfPending && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading PDF...</p>
            </div>
          </div>
        )}

        {pdfPending && (
          <PdfPendingNotice variant={pdfPending} onRetry={() => setRetryCount((n) => n + 1)}>
            <button
              onClick={() => window.history.length > 1 ? navigate(-1) : navigate(`/clients/${clientSlug}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Go Back
            </button>
          </PdfPendingNotice>
        )}

        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md px-4">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load PDF</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => setRetryCount((n) => n + 1)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  Retry
                </button>
                <button
                  onClick={() => window.history.length > 1 ? navigate(-1) : navigate(`/clients/${clientSlug}`)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        )}

        {pdfData && !isLoading && !error && !pdfPending && (
          <iframe
            src={pdfData}
            className="w-full h-full border-0"
            title={document.name}
          />
        )}
      </div>
    </div>
  );
}
